import React from "react";
import {
  makeStyles,
  createStyles,
  Theme,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Button,
  Container,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import {
  PageDisplay,
  ColumnBaseHeaderStyle,
  ColumnBaseCellStyle,
} from "../../../components";
import { FileCrawlingHistoryPaneData } from "../../../types/notification";

/** スタイル定義 */
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    table: {
      minWidth: 650,
    },
    trBorderTop: {
      borderTop: "1px solid rgba(224, 224, 224, 1)",
    },
    trBorderBottom: {
      borderBottom: "1px solid rgba(224, 224, 224, 1)",
    },
    th: {
      ...ColumnBaseHeaderStyle,
      lineHeight: "1.5rem",
      backgroundColor: theme.palette.background.paper,
    },
    thCount: {
      ...ColumnBaseHeaderStyle,
      minWidth: "4rem",
      textAlign: "center",
    },
    td: ColumnBaseCellStyle,
    tdNumber: {
      ...ColumnBaseCellStyle,
      textAlign: "center",
    },
    success: {
      ...ColumnBaseCellStyle,
      color: theme.palette.success.dark,
      whiteSpace: "nowrap",
    },
    error: {
      ...ColumnBaseCellStyle,
      color: theme.palette.error.dark,
      whiteSpace: "nowrap",
    },
    linkButton: {
      cursor: "pointer",
      textDecoration: "underline",
      justifyContent: "flex-end",
      margin: 0,
      padding: 0,
      minWidth: "1rem",
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
    tableContainer: {
      marginTop: theme.spacing(1),
      maxHeight: "80vh",
    },
    pagination: {
      marginTop: theme.spacing(1),
    },
    noData: {
      color: theme.palette.text.secondary,
    },
  })
);

interface Props {
  historyData: FileCrawlingHistoryPaneData;
  onChangeCurrentPage: (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => void;
  onChangeOpenErrorPane: (id: number) => void;
}

/** Fileクロール履歴ペイン */
export function FileCrawlingHistoryPane(props: Props) {
  const classes = useStyles();

  //------------------------------
  // 履歴画面データ
  // [State]
  const { historyData } = props;

  // [Handler]
  const { onChangeCurrentPage } = props;

  //------------------------------
  // エラー画面表示
  // [Handler]
  const { onChangeOpenErrorPane } = props;

  // データなしの場合の表示
  const noData = <div className={classes.noData}>(No data)</div>;

  //--------------------
  // コンポーネント
  return (
    <Container maxWidth="lg">
      <PageDisplay
        fromNumber={
          (historyData.currentPage - 1) * historyData.displayCount + 1
        }
        toNumber={
          historyData.currentPage * historyData.displayCount <
          historyData.totalCount
            ? historyData.currentPage * historyData.displayCount
            : historyData.totalCount
        }
        totalNumber={historyData.totalCount}
      />
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table
          className={classes.table}
          size="small"
          stickyHeader
          aria-label="a dense table"
        >
          <TableHead>
            <TableRow>
              <td rowSpan={2} className={classes.th}>
                No
              </td>
              <td rowSpan={2} className={classes.th} style={{ width: "8rem" }}>
                開始日時
                <br />
                終了日時
              </td>
              <td rowSpan={2} className={classes.th} style={{ width: "4rem" }}>
                所要時間
              </td>
              <td rowSpan={2} className={classes.th} style={{ width: "8rem" }}>
                接続先
              </td>
              <td className={classes.th}>結果</td>
              <td className={classes.thCount}>追加</td>
              <td className={classes.thCount}>変更</td>
              <td className={classes.thCount}>削除</td>
              <td className={classes.thCount}>変更なし</td>
              <td className={classes.thCount}>エラー</td>
            </TableRow>
            <TableRow>
              <td colSpan={6} className={classes.th}>
                メッセージ
              </td>
            </TableRow>
          </TableHead>
          <TableBody>
            {historyData.rows.map((row, i) => [
              <TableRow key={`${row.id}-1`} className={classes.trBorderTop}>
                <td rowSpan={2} className={classes.td}>
                  {(historyData.currentPage - 1) * historyData.displayCount +
                    i +
                    1}
                </td>
                <td className={classes.td}>{row.start_datetime || noData}</td>
                <td rowSpan={2} className={classes.td}>
                  {row.required_time || noData}
                </td>
                <td rowSpan={2} className={classes.td}>
                  {row.file_crawling_config.connection.name || noData}
                </td>
                <td
                  className={
                    row.result_status === "成功"
                      ? classes.success
                      : classes.error
                  }
                >
                  {row.result_status || noData}
                </td>
                <td className={classes.tdNumber}>
                  {row.count_create.toLocaleString()}
                </td>
                <td className={classes.tdNumber}>
                  {row.count_update.toLocaleString()}
                </td>
                <td className={classes.tdNumber}>
                  {row.count_delete.toLocaleString()}
                </td>
                <td className={classes.tdNumber}>
                  {row.count_other.toLocaleString()}
                </td>
                <td className={classes.tdNumber}>
                  {row.count_error === 0 ? (
                    row.count_error
                  ) : (
                    <Button
                      className={classes.linkButton}
                      color="primary"
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      onClick={_error => onChangeOpenErrorPane(row.id)}
                    >
                      {row.count_error.toLocaleString()}
                    </Button>
                  )}
                </td>
              </TableRow>,
              <TableRow key={`${row.id}-2`} className={classes.trBorderBottom}>
                <td className={classes.td}>{row.end_datetime || noData}</td>
                <td colSpan={6} className={classes.td}>
                  {row.message || noData}
                </td>
              </TableRow>,
            ])}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination
        className={classes.pagination}
        count={historyData.totalPage}
        page={historyData.currentPage}
        onChange={onChangeCurrentPage}
      />
    </Container>
  );
}
